import moment from 'moment';
import useFetchHomeFashionData from 'Pages/Blog/Hooks/useFetchHomeFashionData';
import { useGetViewType } from 'Pages/Home/Hooks/useGetViewType';
import React from 'react';
import KickImage from 'Reuseables/KickImage';
import Arrow from '../../Molecules/Arrow';
import ListItem from '../../Molecules/ListItem';

import ReactPlaceholder from 'react-placeholder';
import 'react-placeholder/lib/reactPlaceholder.css';
import { TextBlock, RectShape } from 'react-placeholder/lib/placeholders';

import { IListProps } from './List.definitions';

/** CSS import */
import Styles from './List.module.scss';

const List: React.FC<IListProps> = ({}: IListProps) => {
  const { fetchFashionData, data, isError, isFetching, error } =
    useFetchHomeFashionData();

  const itemScroll = React.useRef<HTMLDivElement>(null);
  const [scrollLeftValue, setScrollLeftValue] = React.useState<number>(0);
  const [isHovered, setIsHovered] = React.useState<boolean>(false);

  React.useEffect(() => {
    fetchFashionData();
  }, []);

  const { isMobileView } = useGetViewType();

  const onScrollItems = () => {
    if (!itemScroll.current) {
      return;
    }

    const {
      current: { scrollWidth, clientWidth, scrollLeft },
    } = itemScroll;

    const scrollLeftCeil = Math.ceil(scrollLeft);
    const maxScrollWidth = scrollWidth - clientWidth;

    if (scrollLeftCeil % 100 === 0) {
      setScrollLeftValue(scrollLeftCeil);
    }

    if (scrollLeftCeil >= maxScrollWidth) {
      setScrollLeftValue(scrollLeftCeil);
    }

    if (scrollLeft === 0) {
      setScrollLeftValue(0);
    }
  };

  const isShowingArrow = (arrow: 'left' | 'right'): boolean => {
    if (!itemScroll.current) {
      return false;
    }

    if (!isHovered) {
      return false;
    }

    if (itemScroll.current.scrollWidth <= itemScroll.current.clientWidth) {
      return false;
    }

    if (arrow === 'left') {
      return scrollLeftValue > 0;
    }

    if (arrow === 'right') {
      const maxScrollWidth =
        itemScroll.current.scrollWidth - itemScroll.current.clientWidth;
      return scrollLeftValue < maxScrollWidth;
    }

    return false;
  };

  const rightArrowClicked = () => {
    if (!itemScroll.current) {
      return;
    }

    const scrolledNow = Math.ceil(itemScroll.current.scrollLeft);
    const maxScrollWidth =
      itemScroll.current.scrollWidth - itemScroll.current.clientWidth;

    if (scrolledNow >= maxScrollWidth) {
      return;
    }

    const interval = setInterval(() => {
      if (!itemScroll.current) {
        return;
      }

      itemScroll.current.scrollLeft += isMobileView ? 1.49 : 4;
    }, 1);

    setTimeout(() => {
      clearInterval(interval);

      if (!itemScroll.current) {
        return;
      }

      setScrollLeftValue(Math.ceil(itemScroll.current.scrollLeft));
    }, 1000);
  };

  const leftArrowClicked = () => {
    if (!itemScroll.current) {
      return;
    }

    const scrolledNow = Math.ceil(itemScroll.current.scrollLeft);
    const maxScrollWidth = 0;

    if (scrolledNow <= maxScrollWidth) {
      return;
    }

    const interval = setInterval(() => {
      if (!itemScroll.current) {
        return;
      }

      itemScroll.current.scrollLeft -= isMobileView ? 1.49 : 4;
    }, 1);

    setTimeout(() => {
      clearInterval(interval);

      if (!itemScroll.current) {
        return;
      }

      setScrollLeftValue(Math.ceil(itemScroll.current.scrollLeft));
    }, 1000);
  };

  const Placeholder = React.useMemo(
    () => (
      <div className={Styles['placeholder']}>
        {[...Array(3)].map((_, index) => (
          <div className={Styles['placeholder-item']}>
            <RectShape
              color="#eeeeee"
              style={{ width: '100%', height: '80%', marginBottom: 10 }}
            />
            <TextBlock rows={3} color="#eeeeee" />
          </div>
        ))}
      </div>
    ),
    [],
  );

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Arrow
        type="left"
        onClick={leftArrowClicked}
        show={!isMobileView && isShowingArrow('left')}
      />
      <div
        className={Styles['wrapper']}
        ref={itemScroll}
        onScroll={onScrollItems}
      >
        <ReactPlaceholder ready={!isFetching} customPlaceholder={Placeholder}>
          <>
            {data.map((item, index) => (
              <ListItem
                key={`fashion-article-${item.slug}-${index}`}
                title={item.title}
                date={moment(item.created_at).format('MMMM DD, YYYY')}
                image={item.thumbnail?.URL ?? 'https://picsum.photos/500'}
                link={`/kickavenews/${item.slug}`}
              />
            ))}
            <ListItem isLastItem={true} link={`/kickavenews/fashion`} />
          </>
        </ReactPlaceholder>
      </div>
      <Arrow
        type="right"
        onClick={rightArrowClicked}
        show={!isMobileView && isShowingArrow('right')}
      />
    </div>
  );
};

export default List;
